import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import type { ModifyBufferSlice } from "../stores/modifyBufferStore";

/**
 * Returns the flows the user *currently sees* for a pre-order: the cached
 * `preOrder.flows` overlaid with any modify-mode buffer (edits applied,
 * deletions removed, additions appended). When no slice is supplied or the
 * buffer is empty, falls back to `preOrder.flows`.
 *
 * Use this anywhere a callback needs to validate against what's actually on
 * screen (e.g. zero-qty gate before confirm) when modify-mode may be active.
 */
export function effectivePreOrderFlows(
  preOrder: PreOrder,
  slice?: ModifyBufferSlice,
): PreOrderFlow[] {
  const original = preOrder.flows ?? [];
  // Only overlay buffer when the slice is actually in modify mode. Without
  // this guard, a slice that was drained mid-flight (post-flush, pre-status
  // flip) or stale post-exit could surface phantom edits/additions.
  if (!slice?.isModifying) return original;
  const base = original.filter((f) => !slice.deleted.has(f.id));
  const merged = base.map((f) => {
    const patch = slice.edits[f.id];
    return patch ? ({ ...f, ...patch } as PreOrderFlow) : f;
  });
  return [...merged, ...slice.added];
}
